import { useEffect, useMemo, useState } from 'react';
import useFunctionsUrl from './use-function-url';

type UseFetchItemsResult<T> = {
  items: T[];
  loading: boolean;
  error: boolean;
};

export default function useFetchItems<T>(
  endpoint: string,
  resultKey: string,
  options: Record<string, string | number | undefined> = {}
): UseFetchItemsResult<T> {
  const baseUrl = useFunctionsUrl();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
    return `${baseUrl}/${endpoint}?${params.toString()}`;
  }, [baseUrl, endpoint, JSON.stringify(options)]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
        const data = await res.json();
        if (!cancelled) {
          setItems(Array.isArray(data[resultKey]) ? data[resultKey] : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          setError(true);
          setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, resultKey, endpoint]);

  return { items, loading, error };
}
